import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Check } from "lucide-react";

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const ref = useRef(false);

  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const poll = async () => {
      for (let i = 0; i < 30; i++) {
        try {
          const res = await getPaymentStatus(sessionId);
          setAttempts(i + 1);
          if (res.payment_status === "paid" || res.status === "completed") {
            await refresh();
            setStatus("success");
            return;
          }
          if (res.status === "failed" || res.payment_status === "failed" || res.status === "expired") {
            setStatus("failed");
            return;
          }
        } catch (e) {
          // keep trying
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      setStatus("timeout");
    };
    poll();
  }, [location.search, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-zinc-950 p-6">
      <div data-testid="payment-success" className="nb-card p-10 max-w-md text-center nb-shadow-xl">
        {status === "polling" && (
          <>
            <div className="font-display text-3xl font-extrabold mb-2">Verifying payment…</div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium">Checking with Stripe ({attempts}/30)</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 nb-border rounded-full bg-[#9DFFB0] flex items-center justify-center">
              <Check className="w-9 h-9 text-black" strokeWidth={3} />
            </div>
            <div className="font-display text-3xl font-extrabold mb-2">You&apos;re Premium!</div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6">
              Full GCSE curriculum unlocked, Maths, Biology and Chemistry. Time to revise like a boss.
            </p>
            <button onClick={() => navigate("/")} data-testid="go-to-app" className="nb-button px-6 py-3 bg-[#D4C4FB] dark:bg-[#B794F4]">
              Open ReviseAI
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="font-display text-3xl font-extrabold mb-2">Payment failed</div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6">
              No charge was made. Try again or contact support.
            </p>
            <button onClick={() => navigate("/pricing")} className="nb-button px-5 py-3">Back to pricing</button>
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="font-display text-2xl font-extrabold mb-2">Still processing</div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-6">
              Your payment is still being verified. Refresh in a minute.
            </p>
            <button onClick={() => window.location.reload()} className="nb-button px-5 py-3">Refresh</button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="font-display text-2xl font-extrabold mb-2">Missing session</div>
            <button onClick={() => navigate("/pricing")} className="nb-button px-5 py-3">Back to pricing</button>
          </>
        )}
      </div>
    </div>
  );
}
